import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import type { FetchReturn, IAssemblyListing } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapAssemblyListingData } from './helpers';

export const useGetAssemblyListing = (filters: string[] = [], enabled = true) =>
  useInfiniteQuery({
    queryKey: queryKeys.assemblyListing.pagination(filters),
    queryFn: ({ pageParam = 1 }) => fetchAssemblyListing(pageParam, filters),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: enabled && !isEmpty(filters),
  });

const fetchAssemblyListing = async (page: number, filters: string[] = []) => {
  const response = await requestCCApi({
    url: `/assemblyListing${generateQuery(
      [],
      concat(filters, ['count=true', `page=${page}`]),
    )}`,
  });

  const assemblyIds = response?.data.map((assembly: IAssemblyListing) =>
    assembly.id.toString(),
  );

  const parentIds =
    response?.data
      .filter((assembly: IAssemblyListing) => assembly.variation_parent_id)
      .map((assembly: IAssemblyListing) =>
        assembly.variation_parent_id!.toString(),
      ) || [];

  const [childrenAssemblies, siblingAssemblies] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: queryKeys.assemblyListing.all(
        `variation_parent_id.list=${assemblyIds.join('|')}`,
      ),
      queryFn: () =>
        fetchAssemblyListingWithoutPagination([
          `variation_parent_id.list=${assemblyIds.join('|')}`,
        ]),
      staleTime: Infinity,
    }),

    parentIds.length > 0
      ? queryClient.fetchQuery({
          queryKey: queryKeys.assemblyListing.all(
            `variation_parent_id.list=${parentIds.join('|')}`,
          ),
          queryFn: () =>
            fetchAssemblyListingWithoutPagination([
              `variation_parent_id.list=${parentIds.join('|')}`,
            ]),
          staleTime: Infinity,
        })
      : Promise.resolve([]),
  ]);

  const assembliesWithRelations = response?.data.map(
    (assembly: IAssemblyListing) => {
      const children = childrenAssemblies.filter(
        (childAssembly: IAssemblyListing) =>
          childAssembly.variation_parent_id === assembly.id,
      );

      const siblings = assembly.variation_parent_id
        ? siblingAssemblies.filter(
            (siblingAssembly: IAssemblyListing) =>
              siblingAssembly.variation_parent_id ===
                assembly.variation_parent_id &&
              siblingAssembly.id !== assembly.id,
          )
        : [];

      return {
        ...assembly,
        childrenAssemblies: children,
        siblingAssemblies: siblings,
      };
    },
  );

  return {
    assemblies: mapAssemblyListingData(assembliesWithRelations),
    totalPages: response?.totalPages ?? 0,
    totalCount: response?.totalCount ?? 0,
  };
};

export const useGetAssemblyListingWithoutPagination = (
  filters: string[] = [],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: queryKeys.assemblyListing.all(concat(filters, ['pageSize=100'])),
    queryFn: () => fetchAssemblyListingWithoutPagination(filters),
    enabled: enabled,
    staleTime: Infinity,
  });

export const fetchAssemblyListingWithoutPagination = async (
  filters: string[] = [],
) => {
  const response = await requestCCApi({
    url: `/assemblyListing${generateQuery(
      [],
      concat(filters, ['pageSize=100']),
    )}`,
  });

  return mapAssemblyListingData(response.data);
};

export const useGetAssemblyListingWithoutInclude = (
  filters: string[],
  overrideQuerySetting?: UseQueryOptions<IAssemblyListing[]>,
) => {
  return useQuery({
    queryKey: queryKeys.assemblyListing.all(filters),
    queryFn: () =>
      fetchAssemblyListingWithoutInclude(concat(filters, 'pageSize=100')),

    ...overrideQuerySetting,
  });
};

export const fetchAssemblyListingWithoutInclude = async <
  T extends boolean = false,
>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IAssemblyListing[]>> => {
  const response = await requestCCApi({
    url: `/assemblyListing${generateQuery(
      [],
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  const data = mapAssemblyListingData(response.data);

  if (isPagination) {
    return {
      data: data,
      totalPages: response.totalPages ?? 0,
    } as FetchReturn<T, IAssemblyListing[]>;
  }

  return data as FetchReturn<T, IAssemblyListing[]>;
};
